import React from 'react';
import { motion } from 'framer-motion';
import { Package, DollarSign, Hash, Download, FileText, FileSpreadsheet, File, Calendar } from 'lucide-react';

const ProductCard = ({ product, index }) => {
  const getStockStatus = (stock) => {
    if (stock > 100) return { color: 'text-green-600', bg: 'bg-green-100', label: 'Alto Stock' };
    if (stock > 50) return { color: 'text-yellow-600', bg: 'bg-yellow-100', label: 'Stock Medio' };
    return { color: 'text-red-600', bg: 'bg-red-100', label: 'Stock Bajo' };
  };

  const getFileIcon = (type) => {
    switch (type) {
      case 'pdf': return <FileText className="w-4 h-4" />;
      case 'docx': return <FileText className="w-4 h-4" />; // Usamos el mismo para Word
      case 'xlsx': return <FileSpreadsheet className="w-4 h-4" />;
      default: return <File className="w-4 h-4" />;
    }
  };

  const stockStatus = getStockStatus(product.stock); // Mantener esto si el stock se usa internamente

  return (
    <motion.div
      className="bg-white/90 backdrop-blur-sm rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300 group"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: index * 0.1 }}
      whileHover={{ y: -3 }}
    >
      <div className="relative h-48 overflow-hidden">
        <img 
          src={product.image} 
          alt={product.name}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
        />
        {/* Se elimina el indicador de stock visual en la tarjeta */}
      </div>
      
      <div className="p-6">
        <div className="flex items-start justify-between mb-3">
          <h3 className="text-lg font-bold text-gray-900 line-clamp-2 flex-1">
            {product.name}
          </h3>
        </div>
        
        <p className="text-gray-600 text-sm mb-4 line-clamp-2">
          {product.description}
        </p>
        
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <DollarSign className="w-4 h-4 text-green-600" />
              <span className="text-sm text-gray-500 mr-1">Desde</span>
              <span className="text-2xl font-bold text-green-600">
                ${product.price}
              </span>
            </div>
            <div className="text-right">
              <p className="text-xs text-gray-500">Precio B2B</p>
            </div>
          </div>
          
          <div className="flex items-center justify-between pt-3 border-t border-gray-100">
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4 text-blue-600" /> {/* Icono de calendario */}
              <span className="text-sm font-medium text-gray-700">
                Actualizado: {product.lastUpdated} {/* Mostrar fecha de última actualización */}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <Hash className="w-4 h-4 text-gray-400" />
              <span className="text-sm text-gray-500 font-mono">
                {product.sku}
              </span>
            </div>
          </div>
        </div>
        
        {product.files && product.files.length > 0 ? (
          <div className="mt-4 space-y-2">
            {product.files.map((file, fileIndex) => (
              <motion.a
                key={fileIndex}
                href={file.url}
                target="_blank"
                rel="noopener noreferrer"
                className="w-full flex items-center justify-center gap-2 bg-gradient-to-r from-blue-500 to-purple-500 text-white py-3 rounded-xl font-semibold hover:from-blue-600 hover:to-purple-600 transition-all duration-200 text-sm"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                {getFileIcon(file.type)}
                Descargar {file.name.split('.').pop().toUpperCase()}
                <Download className="w-4 h-4" />
              </motion.a>
            ))}
          </div>
        ) : (
          <motion.button
            className="w-full mt-4 bg-gray-200 text-gray-600 py-3 rounded-xl font-semibold cursor-not-allowed"
            disabled
          >
            No hay archivos disponibles
          </motion.button>
        )}
      </div>
    </motion.div>
  );
};

export default ProductCard;